import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, ListItem, ListItemText, Paper, CircularProgress, Alert, Box, Button, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import Sidebar from '../components/Sidebar';
import { PageContainer, ContentContainer, Header } from '../components/ContentContainer';
import config from '../config';

const PomScenesToolPage = () => {
  const navigate = useNavigate();
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [apiBase, setApiBase] = useState('');
  const [scenesRootAbs, setScenesRootAbs] = useState('');
  const [scenesRootUrl, setScenesRootUrl] = useState('');
  const [filterName, setFilterName] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const normalizePath = (p) => {
    if (!p) return p;
    let s = p.replace(/\\/g, '/');
    s = s.replace(/\/\.{1}\//g, '/');
    s = s.replace(/\/{2,}/g, '/');
    return s;
  };

  const makeRelToScenes = (imgPath, dirRel) => {
    let rel = normalizePath(String(imgPath || ''));
    const rootMarker = 'data/scenes/';
    const rootMarkerAlt = '/data/scenes/';
    if (rel.toLowerCase().startsWith(rootMarker)) {
      rel = rel.slice(rootMarker.length);
    } else if (rel.toLowerCase().startsWith(rootMarkerAlt)) {
      rel = rel.slice(rootMarkerAlt.length);
    } else if (dirRel) {
      // If it already starts with dirRel, keep; else join
      const dirRelNorm = normalizePath(String(dirRel)).replace(/^\//, '');
      if (!rel.toLowerCase().startsWith(dirRelNorm.toLowerCase())) {
        rel = normalizePath(`${dirRelNorm}/${rel}`);
      }
    }
    // Remove any accidental leading './'
    rel = rel.replace(/^\.\//, '');
    return rel.replace(/^\//, '');
  };

  useEffect(() => {
    let aborted = false;
    (async () => {
      setLoading(true);
      setError('');
      const endpoints = [
        `/pom-scenes`,
        `http://localhost:8000/pom-scenes`,
        `http://127.0.0.1:8000/pom-scenes`,
        `${config.serverUrl}/pom-scenes`
      ];
      let lastError = '';
      for (const url of endpoints) {
        try {
          const res = await fetch(url);
          if (!res.ok) {
            // try to parse error details if JSON
            let detail = '';
            try { const d = await res.json(); detail = d && d.detail; } catch {}
            lastError = detail || `HTTP ${res.status}`;
            continue;
          }
          const data = await res.json();
          let items = Array.isArray(data.scenes) ? data.scenes : [];
          // normalize to objects
          items = items.map((it) => {
            if (typeof it === 'string') {
              return { blend: it };
            }
            return it || {};
          });
          if (!aborted) {
            setScenes(items);
            if (typeof data.scenes_root_abs === 'string') setScenesRootAbs(data.scenes_root_abs);
            if (typeof data.scenes_root_url === 'string') setScenesRootUrl(data.scenes_root_url);
            // detect base used
            try {
              const u = new URL(url, window.location.origin);
              const base = `${u.protocol}//${u.host}` + (u.pathname.startsWith('/pom-scenes') ? '' : u.pathname.replace(/\/pom-scenes.*$/, ''));
              setApiBase(base);
            } catch {
              setApiBase('');
            }
          }
          lastError = '';
          break;
        } catch (e) {
          lastError = e && e.message ? e.message : 'Network error';
          continue;
        }
      }
      if (!aborted) {
        if (lastError) setError(lastError);
        setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, []);

  return (
    <PageContainer>
      <Sidebar navigate={navigate} />
      <ContentContainer>
        <Header>POM scenes tool</Header>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/pom-scenes/add')}
          >
            Add new scene
          </Button>
        </Box>
        {loading && <CircularProgress />}
        {!loading && error && (
          <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
        )}
        {!loading && !error && (
          <Paper sx={{ width: '100%', maxWidth: 900, p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Filter by name"
                size="small"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="sort-label">Sort</InputLabel>
                <Select
                  labelId="sort-label"
                  label="Sort"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <MenuItem value="asc">Name A → Z</MenuItem>
                  <MenuItem value="desc">Name Z → A</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <List>
              {scenes.length === 0 && (
                <ListItem>
                  <ListItemText primary="No scenes found." />
                </ListItem>
              )}
              {(function() {
                const toName = (blendPath) => {
                  const normalizedBlend = (blendPath || '').replace(/\\/g, '/');
                  return normalizedBlend ? normalizedBlend.split('/').pop() : 'Scene';
                };
                const normalized = scenes.map((scene) => ({ scene, name: toName(scene.blend || '') }));
                const filtered = filterName
                  ? normalized.filter((it) => it.name.toLowerCase().includes(filterName.toLowerCase()))
                  : normalized;
                filtered.sort((a, b) => {
                  const cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
                  return sortOrder === 'asc' ? cmp : -cmp;
                });
                return filtered.map(({ scene, name }) => {
                const blend = scene.blend || '';
                const normalizedBlend = blend ? blend.replace(/\\/g, '/'): '';
                const imgUrl = scene.img_url || scene.imgUrl || null;
                const imgPath = (scene.img_path || scene.imgPath || '').toString();
                const dirRel = (scene.dir_rel || scene.dirRel || (() => {
                  const dir = normalizedBlend.split('/').slice(0, -1).join('/');
                  const prefix = 'data/scenes/';
                  return dir.startsWith(prefix) ? dir.slice(prefix.length) : dir;
                })());
                let absoluteImg = null;
                const isAbsPath = (p) => /^(?:[a-zA-Z]:\\|\/)/.test(p || '');
                if (imgUrl) {
                  // Build from public URL if provided
                  if (/^https?:\/\//i.test(imgUrl)) {
                    absoluteImg = imgUrl;
                  } else if (apiBase) {
                    absoluteImg = new URL(normalizePath(imgUrl), apiBase + '/').href;
                  } else if (config.serverUrl) {
                    absoluteImg = new URL(normalizePath(imgUrl), config.serverUrl + '/').href;
                  } else {
                    absoluteImg = normalizePath(imgUrl);
                  }
                } else if (imgPath && scenesRootUrl) {
                  const baseUrl = apiBase || config.serverUrl || '';
                  try {
                    if (isAbsPath(imgPath) && scenesRootAbs) {
                      // Map absolute path to URL if under scenesRootAbs
                      const normPath = imgPath.replace(/\\/g, '/');
                      const normRoot = scenesRootAbs.replace(/\\/g, '/');
                      if (normPath.toLowerCase().startsWith(normRoot.toLowerCase())) {
                        let rel = normPath.slice(normRoot.length);
                        if (rel.startsWith('/')) rel = rel.slice(1);
                        const urlPath = normalizePath(`${scenesRootUrl.replace(/\/$/, '')}/${rel}`);
                        absoluteImg = new URL(urlPath, baseUrl ? baseUrl + '/' : window.location.origin + '/').href;
                      }
                    } else {
                      // Relative path: compute relative to scenes root, then join
                      const rel = makeRelToScenes(imgPath, dirRel);
                      const joined = `${scenesRootUrl.replace(/\/$/, '')}/${rel}`;
                      absoluteImg = new URL(normalizePath(joined), baseUrl ? baseUrl + '/' : window.location.origin + '/').href;
                    }
                  } catch {
                    absoluteImg = null;
                  }
                }
                // Absolute filesystem path for display
                let imgPathText = scene.img_path || scene.imgPath || '';
                if (imgPathText && !isAbsPath(imgPathText) && scenesRootAbs) {
                  const rel = makeRelToScenes(imgPathText, dirRel);
                  imgPathText = normalizePath(`${scenesRootAbs.replace(/\/$/, '')}/${rel}`);
                }
                return (
                  <ListItem key={blend || name} divider>
                    {absoluteImg && (
                      <Box
                        component="img"
                        src={absoluteImg}
                        alt={name}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, mr: 2 }}
                      />
                    )}
                    <ListItemText primary={name} secondary={imgPathText} />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => navigate(`/pom-scenes/edit?scene=${encodeURIComponent(blend)}`)}
                      sx={{ ml: 2 }}
                    >
                      Edit
                    </Button>
                  </ListItem>
                );
              }); })()}
            </List>
          </Paper>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default PomScenesToolPage;
